import React, { useState } from 'react';
import { Bell, X, ExternalLink } from 'lucide-react';
import { useNotifications } from '../contexts/NotificationContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';

const NotificationDropdown: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);

  const {
    notifications,
    unreadCount,
    markAsRead,
    markAllAsRead,
  } = useNotifications();

  const { language } = useLanguage();
  const { user } = useAuth();

  /**
   * Check whether the currently logged-in user
   * has a specific role.
   *
   * User.roles is an array, so we use .some()
   * instead of comparing the array directly to a string.
   */
  const hasRole = (roleName: string): boolean => {
    if (!user) return false;
    if (user.role === roleName) return true;
    return user.roles?.some((role) => role.name === roleName) ?? false;
  };

  /**
   * Get notification title based on user's role.
   */
  const getNotificationTitle = (): string => {
    if (hasRole('ministry_admin')) {
      return language === 'en'
        ? 'Grievance Forwarded'
        : 'गुनासो अगाडि पठाइयो';
    }

    return language === 'en'
      ? 'New Grievance'
      : 'नयाँ गुनासो';
  };

  /**
   * Get grievance page according to user's role.
   */
  const getNavigationPath = (): string => {
    if (hasRole('ministry_admin')) {
      return '/ministry/grievances';
    }

    if (hasRole('office_admin')) {
      return '/office/grievances';
    }

    if (hasRole('super_admin')) {
      return '/super-admin/grievances';
    }

    // Safe fallback
    return '/super-admin/grievances';
  };

  /**
   * Format notification date/time.
   */
  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);

    // Handle invalid date
    if (Number.isNaN(date.getTime())) {
      return '';
    }

    const now = new Date();

    const diffInMinutes = Math.floor(
      (now.getTime() - date.getTime()) /
        (1000 * 60)
    );

    if (diffInMinutes < 1) {
      return language === 'en'
        ? 'Just now'
        : 'अहिले';
    }

    if (diffInMinutes < 60) {
      return language === 'en'
        ? `${diffInMinutes}m ago`
        : `${diffInMinutes} मिनेट अघि`;
    }

    if (diffInMinutes < 1440) {
      const hours = Math.floor(
        diffInMinutes / 60
      );

      return language === 'en'
        ? `${hours}h ago`
        : `${hours} घण्टा अघि`;
    }

    return date.toLocaleDateString();
  };

  /**
   * Handle clicking a notification.
   */
  const handleNotificationClick = async (
    notification: any
  ): Promise<void> => {
    try {
      // Mark unread notification as read
      if (!notification.isRead) {
        await markAsRead(notification.id);
      }

      // Close dropdown
      setIsOpen(false);

      // Navigate to appropriate grievance page
      window.location.href =
        getNavigationPath();
    } catch (error) {
      console.error(
        'Failed to handle notification click:',
        error
      );

      // Still close dropdown and navigate
      setIsOpen(false);
      window.location.href =
        getNavigationPath();
    }
  };

  /**
   * Handle "View all grievances".
   */
  const handleViewAll = (): void => {
    setIsOpen(false);
    window.location.href =
      getNavigationPath();
  };

  /**
   * Handle "Mark all as read".
   */
  const handleMarkAllAsRead = async (): Promise<void> => {
    try {
      await markAllAsRead();
    } catch (error) {
      console.error(
        'Failed to mark all notifications as read:',
        error
      );
    }
  };

  return (
    <div className="relative">
      {/* =========================================
          NOTIFICATION BELL
      ========================================== */}
      <button
        type="button"
        onClick={() =>
          setIsOpen((previous) => !previous)
        }
        aria-label={
          language === 'en'
            ? 'Open notifications'
            : 'सूचनाहरू खोल्नुहोस्'
        }
        aria-expanded={isOpen}
        className="
          relative
          p-2
          text-gray-600
          hover:text-gray-900
          focus:outline-none
          focus:ring-2
          focus:ring-blue-500
          focus:ring-offset-2
          rounded-full
          transition-all
          duration-200
          hover:bg-gray-100
        "
      >
        <Bell className="h-6 w-6" />

        {/* Unread count */}
        {unreadCount > 0 && (
          <span
            className="
              absolute
              -top-1
              -right-1
              bg-red-500
              text-white
              text-xs
              rounded-full
              h-5
              min-w-5
              px-1
              flex
              items-center
              justify-center
              font-medium
              shadow-lg
              animate-pulse
            "
          >
            {unreadCount > 99
              ? '99+'
              : unreadCount}
          </span>
        )}
      </button>

      {/* =========================================
          DROPDOWN
      ========================================== */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-40"
            onClick={() => setIsOpen(false)}
            aria-hidden="true"
          />

          {/* Dropdown Content */}
          <div
            className="
              absolute
              right-0
              mt-2
              w-96
              max-w-[calc(100vw-2rem)]
              bg-white
              rounded-xl
              shadow-xl
              border
              border-gray-200
              z-50
              overflow-hidden
            "
          >
            {/* =====================================
                HEADER
            ====================================== */}
            <div
              className="
                flex
                items-center
                justify-between
                p-4
                border-b
                border-gray-100
                bg-gray-50
              "
            >
              <h3 className="text-lg font-semibold text-gray-900">
                {language === 'en'
                  ? 'Notifications'
                  : 'सूचनाहरू'}
              </h3>

              <div className="flex items-center space-x-2">
                {/* Mark all as read */}
                {unreadCount > 0 && (
                  <button
                    type="button"
                    onClick={handleMarkAllAsRead}
                    className="
                      text-sm
                      text-blue-600
                      hover:text-blue-800
                      font-medium
                      transition-colors
                    "
                  >
                    {language === 'en'
                      ? 'Mark all read'
                      : 'सबै पढ्नुहोस्'}
                  </button>
                )}

                {/* Close */}
                <button
                  type="button"
                  onClick={() =>
                    setIsOpen(false)
                  }
                  aria-label={
                    language === 'en'
                      ? 'Close notifications'
                      : 'सूचना बन्द गर्नुहोस्'
                  }
                  className="
                    text-gray-400
                    hover:text-gray-600
                    transition-colors
                  "
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* =====================================
                NOTIFICATIONS LIST
            ====================================== */}
            <div className="max-h-80 overflow-y-auto">
              {notifications.length === 0 ? (
                /* Empty state */
                <div className="p-8 text-center text-gray-500">
                  <Bell className="h-12 w-12 mx-auto mb-3 text-gray-300" />

                  <p className="text-sm">
                    {language === 'en'
                      ? 'No notifications yet'
                      : 'अहिलेसम्म कुनै सूचना छैन'}
                  </p>
                </div>
              ) : (
                notifications.map(
                  (notification) => (
                    <div
                      key={notification.id}
                      onClick={() =>
                        handleNotificationClick(
                          notification
                        )
                      }
                      role="button"
                      tabIndex={0}
                      onKeyDown={(event) => {
                        if (
                          event.key === 'Enter' ||
                          event.key === ' '
                        ) {
                          event.preventDefault();

                          handleNotificationClick(
                            notification
                          );
                        }
                      }}
                      className={`
                        p-4
                        border-b
                        border-gray-100
                        hover:bg-gray-50
                        cursor-pointer
                        transition-colors
                        ${
                          !notification.isRead
                            ? 'bg-blue-50 border-l-4 border-l-blue-500'
                            : ''
                        }
                      `}
                    >
                      <div className="flex items-start space-x-3">
                        {/* Notification Icon */}
                        <div
                          className={`
                            flex-shrink-0
                            w-10
                            h-10
                            rounded-full
                            flex
                            items-center
                            justify-center
                            ${
                              !notification.isRead
                                ? 'bg-blue-100 text-blue-600'
                                : 'bg-gray-100 text-gray-600'
                            }
                          `}
                        >
                          <Bell className="h-5 w-5" />
                        </div>

                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          {/* Title + Date */}
                          <div className="flex items-center justify-between gap-2 mb-1">
                            <h4 className="text-sm font-medium text-gray-900 truncate">
                              {getNotificationTitle()}
                            </h4>

                            <div className="flex items-center space-x-2 flex-shrink-0">
                              <span className="text-xs text-gray-500">
                                {formatDate(
                                  notification.createdAt
                                )}
                              </span>

                              {!notification.isRead && (
                                <div className="w-2 h-2 bg-blue-500 rounded-full" />
                              )}
                            </div>
                          </div>

                          {/* Notification Message */}
                          <p className="text-sm text-gray-600 mb-2">
                            {notification.message}
                          </p>

                          {/* Grievance Details */}
                          {notification.grievance && (
                            <div
                              className="
                                bg-gray-50
                                rounded-lg
                                p-3
                                mb-2
                                border
                                border-gray-200
                              "
                            >
                              <div className="text-sm">
                                {/* Subject */}
                                <div className="font-medium text-gray-900 mb-1 truncate">
                                  {
                                    notification
                                      .grievance
                                      .subject
                                  }
                                </div>

                                {/* Description */}
                                <div className="text-gray-600 text-xs mb-2 line-clamp-2">
                                  {notification.grievance.description?.substring(
                                    0,
                                    120
                                  )}

                                  {notification.grievance
                                    .description
                                    ?.length >
                                    120 && '...'}
                                </div>

                                {/* ID + Status */}
                                <div className="flex items-center justify-between gap-2 text-xs">
                                  <span className="text-gray-500 font-mono truncate">
                                    ID:{' '}
                                    {
                                      notification
                                        .grievance
                                        .grievanceId
                                    }
                                  </span>

                                  <span
                                    className={`
                                      px-2
                                      py-1
                                      rounded-full
                                      text-xs
                                      font-medium
                                      flex-shrink-0
                                      ${
                                        notification
                                          .grievance
                                          .status ===
                                        'pending'
                                          ? 'bg-yellow-100 text-yellow-800'
                                          : notification
                                                .grievance
                                                .status ===
                                              'in-progress'
                                            ? 'bg-blue-100 text-blue-800'
                                            : notification
                                                  .grievance
                                                  .status ===
                                                'resolved'
                                              ? 'bg-green-100 text-green-800'
                                              : 'bg-red-100 text-red-800'
                                      }
                                    `}
                                  >
                                    {
                                      notification
                                        .grievance
                                        .status
                                    }
                                  </span>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>

                        {/* External Link */}
                        <div className="flex-shrink-0">
                          <ExternalLink className="h-4 w-4 text-gray-400" />
                        </div>
                      </div>
                    </div>
                  )
                )
              )}
            </div>

            {/* =====================================
                FOOTER
            ====================================== */}
            {notifications.length > 0 && (
              <div
                className="
                  p-3
                  border-t
                  border-gray-100
                  bg-gray-50
                  text-center
                "
              >
                <button
                  type="button"
                  onClick={handleViewAll}
                  className="
                    text-sm
                    text-blue-600
                    hover:text-blue-800
                    font-medium
                    transition-colors
                  "
                >
                  {language === 'en'
                    ? 'View all grievances'
                    : 'सबै गुनासो हेर्नुहोस्'}
                </button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default NotificationDropdown;